import { forwardRef, useState, type ReactNode } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

/**
 * Reusable Cloudflare Turnstile mount — the ONE client widget for
 * every captcha-gated admin form (today: the SP install-registration
 * wizard). Admin-side copy of apps/web/src/components/ui/
 * TurnstileGate.tsx, ported verbatim so the two apps share the same
 * token-lifecycle convention without a cross-app import.
 *
 * Wraps @marsidev/react-turnstile with the project's token-lifecycle
 * convention: onToken(token) on a solved challenge, onToken(null) on
 * error and expiry, so callers gate their submit on a live token and
 * call .reset() through the ref after a server-side TURNSTILE_FAILED.
 *
 * The site key is baked in at build time from VITE_TURNSTILE_SITE_KEY.
 * When it is absent the posture prop decides:
 *   - 'closed': render the caller's fallback and the caller keeps its
 *     submit disabled (a captcha gate that silently disappears is
 *     worse than a blocked form).
 *   - 'open': render nothing; the caller submits without a token and
 *     the server's matching fail-open policy accepts (the SP install
 *     flow on a keyless staging keeps working).
 */
export const TURNSTILE_SITE_KEY =
  (import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined) || undefined;

interface TurnstileGateProps {
  /** Receives the token on success, null on error/expiry. */
  onToken: (token: string | null) => void;
  /** Behavior when no site key is baked into the build. */
  posture: 'open' | 'closed';
  /** Rendered instead of the widget under posture 'closed' with no key. */
  fallback?: ReactNode;
  className?: string;
}

export const TurnstileGate = forwardRef<TurnstileInstance, TurnstileGateProps>(
  function TurnstileGate({ onToken, posture, fallback, className }, ref) {
    // api.js load failure (blocked network, extension, Cloudflare
    // outage) fires NO widget callback — only scriptOptions.onError
    // reaches it. Without this the caller's submit stays disabled
    // forever beside an empty container with zero feedback.
    const [scriptFailed, setScriptFailed] = useState(false);
    if (!TURNSTILE_SITE_KEY) {
      return posture === 'closed' ? <>{fallback ?? null}</> : null;
    }
    if (scriptFailed) {
      return (
        <div className={className}>
          {fallback ?? (
            <p role="alert">
              The captcha could not load. Check your connection, then
              refresh to try again.
            </p>
          )}
        </div>
      );
    }
    return (
      <div className={className}>
        <Turnstile
          ref={ref}
          siteKey={TURNSTILE_SITE_KEY}
          onSuccess={(token) => onToken(token)}
          onError={() => onToken(null)}
          onExpire={() => onToken(null)}
          options={{ theme: 'auto', size: 'normal' }}
          scriptOptions={{ onError: () => setScriptFailed(true) }}
        />
      </div>
    );
  },
);
